import React from 'react';
import AnalyticsNavigationTabs from '../../../../../uiToolkit/Analytics/AnalyticsNavigationTabs';
import {courseLevelAnalyticsTabs} from '../../conf';
import AnalyticsEmptyState from '../../../../../uiToolkit/Analytics/AnalyticsEmptyState';
import {AnalyticsTableSkeleton} from '../../../components/AnalyticsSkeletons';
import {MicroreelLikes} from '../../../../../data/models/Likes/LikeModel';
import {analyticsText} from '../../../analyticsText';
import {Icon} from '../../../../../uiToolkit/Icon/Icon';
import './styles.css';

const getSortValue = (reel: MicroreelLikes, field: string): string | number =>
  field === 'Likes' ? reel.totalLikes : reel.title.toLowerCase();

const likesComparator =
  (field: string, asc: boolean) => (a: MicroreelLikes, b: MicroreelLikes) => {
    const valueA = getSortValue(a, field);
    const valueB = getSortValue(b, field);
    if (valueA === valueB) return 0;
    const result = valueA > valueB ? 1 : -1;
    return asc ? result : -result;
  };

interface Props {
  organizationId: string;
  courseId: string;
  likes: MicroreelLikes[];
  isLoading: boolean;
  search: string;
  setSearch: (search: string) => void;
  onExport: () => void;
  sortBy: string;
  sortAsc: boolean;
  onChangeSort: (field: string, asc: boolean) => void;
}

const CourseAnalyticsLikesUI = React.memo(
  ({
    organizationId,
    courseId,
    likes,
    isLoading,
    search,
    setSearch,
    onExport,
    sortBy,
    sortAsc,
    onChangeSort,
  }: Props) => {
    const getReels = () => {
      const filtered = likes.filter(reel =>
        reel.title.toLowerCase().includes(search.toLowerCase()),
      );
      return sortBy
        ? filtered.sort(likesComparator(sortBy, sortAsc))
        : filtered;
    };

    const getSortingArrow = (field: string) => {
      if (sortBy === field) {
        if (sortAsc) {
          return <div className="arrow-down"></div>;
        } else {
          return <div className="arrow-up"></div>;
        }
      }
      return <div />;
    };

    const totalLikes = likes.reduce((sum, reel) => sum + reel.totalLikes, 0);

    return (
      <div>
        <AnalyticsNavigationTabs
          onExport={onExport}
          search={search}
          setSearch={setSearch}
          tabs={courseLevelAnalyticsTabs(organizationId, courseId)}
        />
        {isLoading ? (
          <AnalyticsTableSkeleton />
        ) : !likes.length ? (
          <AnalyticsEmptyState title={analyticsText.noLikes} showInfo={false} />
        ) : (
          <>
            <div className="TotalLikesContainer">
              <Icon name="heart.svg" />
              <span className="InfoText">
                {analyticsText.totalLikes(totalLikes)}
              </span>
            </div>
            <div className="CourseLikesAnalyticsTableContainer">
              <table className="CourseLikesAnalyticsTable">
                <thead>
                  <tr>
                    <th className="MicroreelColumn">
                      <div
                        className="SortArrowContainer"
                        onClick={() => onChangeSort('Microreel', !sortAsc)}>
                        {analyticsText.microreel}
                        {getSortingArrow('Microreel')}
                      </div>
                    </th>
                    <th className="LikesColumn">
                      <div
                        className="SortArrowContainer"
                        onClick={() => onChangeSort('Likes', !sortAsc)}>
                        {analyticsText.likes}
                        {getSortingArrow('Likes')}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {getReels().map(reel => (
                    <tr key={reel.lessonId}>
                      <td className="MicroreelColumn">
                        <div className="MicroreelNameContainer">
                          <div className="MicroreelThumb" aria-hidden="true">
                            {reel.poster && (
                              <img
                                className="MicroreelThumbImage"
                                src={reel.poster}
                              />
                            )}
                            <span className="MicroreelPlayBadge">
                              <Icon
                                name="play_ic.svg"
                                className="MicroreelPlayIcon"
                              />
                            </span>
                          </div>
                          <span>{reel.title}</span>
                        </div>
                      </td>
                      <td className="LikesColumn">
                        <div className="LikesCountContainer">
                          <Icon name="heart.svg" />
                          <span>{reel.totalLikes}</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    );
  },
);

export default CourseAnalyticsLikesUI;
